import React, { FC, useCallback, useEffect, useState } from 'react'
import { styled } from '@moonpig/launchpad-utils'
import {
  IconSystemChevronLeft,
  IconSystemChevronRight,
} from '@moonpig/launchpad-assets'
import type { EmblaCarouselType } from 'embla-carousel'
import { colorValue } from '@moonpig/launchpad-theme'
import { useLocaleText } from '../../ProductTile.locale'
import { CAROUSEL_HOVER_MEDIA } from './constants'

const GiftArrowButtonInner = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 100%;
  color: white;
  transition: background 200ms;
`

const StyledControls = styled.div<{ $isHovered?: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  pointer-events: none;
  opacity: 0;
  transition: opacity 200ms ease;

  & > button {
    pointer-events: none;
  }

  ${CAROUSEL_HOVER_MEDIA} {
    opacity: ${({ $isHovered }) => ($isHovered ? 1 : 0)};

    & > button {
      pointer-events: ${({ $isHovered }) => ($isHovered ? 'auto' : 'none')};
    }
  }

  &:focus-within {
    opacity: 1;

    & > button {
      pointer-events: auto;
    }
  }
`

const StyledPrevButton = styled.button`
  position: absolute;
  left: 8px;
  right: auto;
  top: 0;
  bottom: 0;
  margin: auto 0;
  z-index: 1;
  -webkit-tap-highlight-color: transparent;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:hover ${GiftArrowButtonInner} {
    background: rgba(0, 0, 0, 0.7);
  }

  &:disabled {
    visibility: hidden;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${colorValue('colorPrimary02')};
  }
`

const StyledNextButton = styled.button`
  position: absolute;
  right: 8px;
  left: auto;
  top: 0;
  bottom: 0;
  margin: auto 0;
  z-index: 1;
  -webkit-tap-highlight-color: transparent;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:hover ${GiftArrowButtonInner} {
    background: rgba(0, 0, 0, 0.7);
  }

  &:disabled {
    visibility: hidden;
    cursor: default;
  }
`

type Props = {
  emblaApi?: EmblaCarouselType
  scrollSnaps: number[]
  selectedIndex: number
  setScrollSnaps: (snaps: number[]) => void
  childrenArray: React.ReactNode[]
  childrenCount: number
  isHovered?: boolean
}

export const Controls: FC<Props> = ({
  emblaApi,
  scrollSnaps,
  selectedIndex,
  setScrollSnaps,
  childrenArray,
  childrenCount,
  isHovered,
}) => {
  const t = useLocaleText()
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(false)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(false)

  const onPrevClick = useCallback(() => {
    emblaApi?.scrollPrev()
  }, [emblaApi])

  const onNextClick = useCallback(() => {
    emblaApi?.scrollNext()
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    const syncScrollButtons = () => {
      setPrevBtnDisabled(!emblaApi.canScrollPrev())
      setNextBtnDisabled(!emblaApi.canScrollNext())
    }

    syncScrollButtons()
    emblaApi
      .on('select', syncScrollButtons)
      .on('reInit', syncScrollButtons)
      .on('settle', syncScrollButtons)

    return () => {
      emblaApi
        .off('select', syncScrollButtons)
        .off('reInit', syncScrollButtons)
        .off('settle', syncScrollButtons)
    }
  }, [emblaApi])

  useEffect(() => {
    if (emblaApi) return

    if (scrollSnaps.length === 0 && childrenCount > 0) {
      setScrollSnaps(childrenArray.map((_, index) => index))
    }

    const snapCount =
      scrollSnaps.length > 0 ? scrollSnaps.length : childrenCount
    const shouldDisableControls = snapCount <= 1

    setPrevBtnDisabled(shouldDisableControls || selectedIndex === 0)
    setNextBtnDisabled(shouldDisableControls || selectedIndex >= snapCount - 1)
  }, [
    childrenArray,
    childrenCount,
    emblaApi,
    scrollSnaps.length,
    selectedIndex,
    setScrollSnaps,
  ])

  return (
    <StyledControls
      role="group"
      aria-label={t('carousel.controls')}
      $isHovered={!!isHovered}
    >
      <StyledPrevButton
        type="button"
        aria-label={t('carousel.previous_slide')}
        onClick={onPrevClick}
        disabled={prevBtnDisabled}
      >
        <GiftArrowButtonInner>
          <IconSystemChevronLeft width="1.25rem" />
        </GiftArrowButtonInner>
      </StyledPrevButton>
      <StyledNextButton
        type="button"
        aria-label={t('carousel.next_slide')}
        onClick={onNextClick}
        disabled={nextBtnDisabled}
      >
        <GiftArrowButtonInner>
          <IconSystemChevronRight width="1.25rem" />
        </GiftArrowButtonInner>
      </StyledNextButton>
    </StyledControls>
  )
}
